import React, { useEffect, useRef, useState, useCallback } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useNavigate, useOutletContext } from 'react-router-dom';
import animateScrollTo from 'animated-scroll-to';

import styles from './comparison-table-styles.module.css';
import axios from '../../utils/axios';

import ComparisonTableHighlighted from './ComparisonTableHighlighted';

import SD1 from './tables/SD1';
import SD2 from './tables/SD2';
import SD3 from './tables/SD3';
import SD4 from './tables/SD4';
import SD5 from './tables/SD5';
import SD6 from './tables/SD6';
import SD9 from './tables/SD9';
import SD10 from './tables/SD10';
import SD11 from './tables/SD11';
import SD12 from './tables/SD12';

import { useTableStore } from '../../store/useTableStore';
import { useFooterStore } from '../../store/useFooterStore';
import LoaderComponent from '../../components/TransitionComponent/LoaderContent';
import { Button, Icon } from '../../components';
import { LinkedButton } from '../../components/Button';
import { Icons } from '../../components/Icon/Icon';
import { dispatchAsidePortalContentChanged } from '../../utils/aside-portal-event';
import { type LegalStructuresLayoutContextType } from '../LegalStructures/LegalStructuresLayout';

const ComparisonTable = () => {
    // Get context from LegalStructuresLayout
    const context = useOutletContext<LegalStructuresLayoutContextType>();
    const { selectedData, setOnEdit, pageReady, checkClicked, toggleRequest, setToggleRequest } = context;

    const [ready, setReady] = useState(false);

    const [data, setTableData] = useTableStore((state) => [state.data, state.setTableData]);
    const [setShowFooter] = useFooterStore((state) => [state.setShowFooter]);

    const [triggerCollapse, setTriggerCollapse] = useState(false);
    const [allCollapsed, setAllCollapsed] = useState(true);
    const [tablesCollapsed, setTablesCollapsed] = useState(0);
    const [collapseOnClick, setCollapseOnClick] = useState(false);

    const navigate = useNavigate();

    const getComparisonTableData = async (_checkClicked: boolean) => {
        if (!selectedData || !selectedData.assets.length || !selectedData.investors.length) {
            return;
        }

        dispatchAsidePortalContentChanged(true);

        const data = await axios.get('/legal-structures/detailed', {
            params: selectedData,
        });

        setTableData(data.data);

        setTimeout(() => {
            setReady(true);
        }, 100);
    };

    // Fetch data on page ready
    useEffect(() => {
        if (pageReady && selectedData.assets.length > 0 && selectedData.investors.length > 0) {
            (async () => await getComparisonTableData(false))();
        }
    }, [pageReady, selectedData.assets.length, selectedData.investors.length]);

    // Fetch data on toggle request
    useEffect(() => {
        if (toggleRequest) {
            (async () => await getComparisonTableData(true))();
        }
    }, [toggleRequest]);

    // Fetch data on check clicked
    useEffect(() => {
        if (checkClicked) {
            (async () => await getComparisonTableData(false))();
        }
    }, [checkClicked]);

    useEffect(() => {
        setShowFooter(false);
        return () => setShowFooter(true);
    }, []);

    useEffect(() => {
        setAllCollapsed(tablesCollapsed > 0);
        setShowFooter(tablesCollapsed > 0);
    }, [tablesCollapsed]);

    useEffect(() => {
        if (collapseOnClick) {
            setTriggerCollapse(allCollapsed);
        }
    }, [allCollapsed, collapseOnClick]);

    useEffect(() => {
        setTimeout(() => {
            setCollapseOnClick(false);
        }, 1);
    }, [allCollapsed]);

    const collapseAll = () => {
        setCollapseOnClick(true);
        setAllCollapsed(true);
        setTablesCollapsed(0);
    };

    const scrollToTop = async () => {
        await animateScrollTo(0);
    };

    // Set handleEdit for this page
    useEffect(() => {
        if (setOnEdit) {
            const handleEdit = () => {
                // setIsMenuOpen(false);
                // dispatchAsidePortalContentChanged(false);
                // Navigate back to legal-structures page (graph view)
                navigate('/legal-structures' + window.location.search);

                // setToggleRequest(true);
            };
            setOnEdit(handleEdit);
        }
    }, []);

    const ref = useRef(null);

    return (
        <LoaderComponent inProp={ready}>
            {data && (
                <div className={styles.page}>
                    <div className={styles.container}>
                        <div className={styles.comparisonPageHeader}>
                            <h1 className={'text-grey'}>
                                Best-fit <span className={'highlight-secondary'}>legal structures</span>: expanded view
                            </h1>

                            <div>
                                <CSSTransition
                                    in={tablesCollapsed > 0}
                                    timeout={300}
                                    classNames={{
                                        appear: styles.fadeAnimation,
                                        appearActive: styles.fadeAnimationActive,
                                        appearDone: styles.fadeAnimationActive,
                                        enter: styles.fadeAnimation,
                                        enterActive: styles.fadeAnimationActive,
                                        enterDone: styles.fadeAnimationActive,
                                        exit: styles.fadeAnimation,
                                        exitActive: styles.fadeAnimation,
                                        exitDone: styles.fadeAnimation,
                                    }}
                                    unmountOnExit
                                    nodeRef={ref}
                                >
                                    <button ref={ref} onClick={collapseAll}>
                                        Collapse all
                                    </button>
                                </CSSTransition>

                                <div className={styles.backBtn}>
                                    {tablesCollapsed === 0 ? (
                                        <LinkedButton
                                            to={{
                                                pathname: '/legal-structures',
                                                search: window.location.search,
                                            }}
                                            size={'sm'}
                                            after={<Icon icon={Icons.return} />}
                                        >
                                            Back to short version
                                        </LinkedButton>
                                    ) : (
                                        <Button onClick={scrollToTop} size={'sm'}>
                                            Back to top
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <SD1
                            triggerCollapse={triggerCollapse}
                            setTablesCollapsed={setTablesCollapsed}
                            tablesCollapsed={tablesCollapsed}
                            collapseOnClick={collapseOnClick}
                        />

                        <SD2
                            triggerCollapse={triggerCollapse}
                            setTablesCollapsed={setTablesCollapsed}
                            tablesCollapsed={tablesCollapsed}
                            collapseOnClick={collapseOnClick}
                        />

                        <SD3
                            triggerCollapse={triggerCollapse}
                            setTablesCollapsed={setTablesCollapsed}
                            tablesCollapsed={tablesCollapsed}
                            collapseOnClick={collapseOnClick}
                        />

                        <SD4
                            triggerCollapse={triggerCollapse}
                            setTablesCollapsed={setTablesCollapsed}
                            tablesCollapsed={tablesCollapsed}
                            collapseOnClick={collapseOnClick}
                        />

                        <SD5
                            triggerCollapse={triggerCollapse}
                            setTablesCollapsed={setTablesCollapsed}
                            tablesCollapsed={tablesCollapsed}
                            collapseOnClick={collapseOnClick}
                        />

                        <SD6
                            triggerCollapse={triggerCollapse}
                            setTablesCollapsed={setTablesCollapsed}
                            tablesCollapsed={tablesCollapsed}
                            collapseOnClick={collapseOnClick}
                        />

                        <SD9
                            triggerCollapse={triggerCollapse}
                            setTablesCollapsed={setTablesCollapsed}
                            tablesCollapsed={tablesCollapsed}
                            collapseOnClick={collapseOnClick}
                        />

                        <SD10
                            triggerCollapse={triggerCollapse}
                            setTablesCollapsed={setTablesCollapsed}
                            tablesCollapsed={tablesCollapsed}
                            collapseOnClick={collapseOnClick}
                        />

                        <SD11
                            triggerCollapse={triggerCollapse}
                            setTablesCollapsed={setTablesCollapsed}
                            tablesCollapsed={tablesCollapsed}
                            collapseOnClick={collapseOnClick}
                        />

                        <SD12
                            triggerCollapse={triggerCollapse}
                            setTablesCollapsed={setTablesCollapsed}
                            tablesCollapsed={tablesCollapsed}
                            collapseOnClick={collapseOnClick}
                        />
                    </div>

                    <ComparisonTableHighlighted
                        triggerCollapse={triggerCollapse}
                        setTablesCollapsed={setTablesCollapsed}
                        tablesCollapsed={tablesCollapsed}
                        collapseOnClick={collapseOnClick}
                    />
                </div>
            )}
        </LoaderComponent>
    );
};

export default ComparisonTable;
